// Registry of annotatable containers per route
// This enables the global annotation provider to automatically apply
// annotation capabilities to specific elements on each page

import type { LucideIcon } from 'lucide-react';
import {
  LayoutDashboard,
  BarChart3,
  BarChart2,
  Search,
  Brain,
  LineChart,
  Activity,
} from 'lucide-react';

export interface AnnotationConfig {
  containerId: string;
  selector: string;
  label?: string;
}

/** Display label and icon per route for Annotation Settings and other UIs */
export const annotationRouteMeta: Record<string, { label: string; icon: LucideIcon }> = {
  '/dashboard': { label: 'Dashboard', icon: LayoutDashboard },
  '/analytics': { label: 'Analytics', icon: BarChart3 },
  '/seo-analytics': { label: 'SEO Analytics', icon: BarChart2 },
  '/saio-dashboard': { label: 'SAIO Dashboard', icon: Activity },
  '/seo-report': { label: 'SEO Report (GSC)', icon: Search },
  '/seo/rankings': { label: 'Rankings', icon: LineChart },
  '/keywords-5ws': { label: 'Keywords (5Ws Framework)', icon: Brain },
};

export const annotationRegistry: Record<string, AnnotationConfig[]> = {
  '/dashboard': [
    { containerId: 'dashboard-metrics', selector: '[data-annotate="metrics"]', label: 'Metrics Overview' },
    { containerId: 'dashboard-chart', selector: '[data-annotate="chart"]', label: 'Chart' },
    { containerId: 'dashboard-recent-activity', selector: '[data-annotate="recent-activity"]', label: 'Recent Activity' },
  ],
  '/analytics': [
    { containerId: 'key-metrics', selector: '[data-annotate="key-metrics"]', label: 'Key Metrics' },
    { containerId: 'traffic-trends', selector: '[data-annotate="traffic-trends"]', label: 'Traffic Trends' },
    { containerId: 'top-pages', selector: '[data-annotate="top-pages"]', label: 'Top Pages' },
    { containerId: 'traffic-sources', selector: '[data-annotate="traffic-sources"]', label: 'Traffic Sources' },
    { containerId: 'traffic-drivers', selector: '[data-annotate="traffic-drivers"]', label: 'Traffic Drivers' },
    { containerId: 'device-split', selector: '[data-annotate="device-split"]', label: 'Device Split' },
    { containerId: 'top-referrers', selector: '[data-annotate="top-referrers"]', label: 'Top Referrers' },
    { containerId: 'hostname-data', selector: '[data-annotate="hostname-data"]', label: 'Hostname Data' },
    { containerId: 'traffic-countries', selector: '[data-annotate="traffic-countries"]', label: 'Traffic Countries' },
    { containerId: 'session-sources', selector: '[data-annotate="session-sources"]', label: 'Session Sources' },
    { containerId: 'session-medium', selector: '[data-annotate="session-medium"]', label: 'Session Medium' },
  ],
  '/seo-analytics': [
    { containerId: 'seo-analytics-metrics', selector: '[data-annotate="seo-analytics-metrics"]', label: 'Key Metrics' },
    { containerId: 'seo-analytics-traffic-trends', selector: '[data-annotate="seo-analytics-traffic-trends"]', label: 'Traffic Trends' },
    { containerId: 'seo-analytics-gsc-chart', selector: '[data-annotate="seo-analytics-gsc-chart"]', label: 'GSC Monthly Chart' },
    { containerId: 'seo-analytics-top-pages', selector: '[data-annotate="seo-analytics-top-pages"]', label: 'Top Pages' },
  ],
  '/saio-dashboard': [
    { containerId: 'saio-overview', selector: '[data-annotate="saio-overview"]', label: 'SAIO Overview' },
    { containerId: 'saio-metrics', selector: '[data-annotate="saio-metrics"]', label: 'Brand Metrics' },
    { containerId: 'saio-competitors', selector: '[data-annotate="saio-competitors"]', label: 'Competitors Matrix' },
    { containerId: 'saio-sentiment', selector: '[data-annotate="saio-sentiment"]', label: 'Sentiment Analysis' },
  ],
  '/seo-report': [
    { containerId: 'seo-overview', selector: '[data-annotate="seo-overview"]', label: 'SEO Overview' },
    { containerId: 'seo-score', selector: '[data-annotate="seo-score"]', label: 'SEO Score' },
    { containerId: 'seo-recommendations', selector: '[data-annotate="seo-recommendations"]', label: 'Recommendations' },
  ],
  '/seo/rankings': [
    { containerId: 'rankings-table', selector: '[data-annotate="rankings-table"]', label: 'Rankings Table' },
    { containerId: 'rankings-chart', selector: '[data-annotate="rankings-chart"]', label: 'Rankings Chart' },
  ],
  '/keywords-5ws': [
    { containerId: '5ws-keyword-results', selector: '[data-annotate="5ws-keyword-results"]', label: '5Ws Results' },
    { containerId: '5ws-keyword-items', selector: '[data-annotate^="5ws-keyword-"]', label: 'Keyword Analysis' },
  ],
};

// Helper to get configs for a route (supports partial matching for dynamic sub-routes only)
export function getAnnotationConfigs(pathname: string): AnnotationConfig[] {
  // Exact match first
  if (annotationRegistry[pathname]) {
    return annotationRegistry[pathname];
  }
  
  // Partial match - only for dynamic sub-routes of registered paths (e.g., /seo/rankings/123)
  // Must match route + "/" to prevent /annotations matching /a
  for (const [route, configs] of Object.entries(annotationRegistry)) {
    if (pathname.startsWith(route + '/')) {
      return configs;
    }
  }
  
  return [];
}

// Get all registered routes
export function getRegisteredRoutes(): string[] {
  return Object.keys(annotationRegistry);
}
